import React, { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, Bot } from "lucide-react";
import { toast } from "sonner";

export default function CreditCards() {
  const [form, setForm] = useState({
    spending_grocery: 700,
    spending_dining: 400,
    spending_gas: 200,
    spending_travel: 300,
    credit_score: "good",
    annual_fee_ok: true,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const match = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/credit-card-match", form);
      setResult(data);
    } catch (e) {
      toast.error("Couldn't match");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-cc">
      <PageHeader eyebrow="Credit Card Match" title="Find the right card for your spending" subtitle="AI ranks cards based on your monthly grocery, dining, gas, and travel spend." />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Grocery/mo</Label>
                <Input type="number" min="0" step="50" value={form.spending_grocery} onChange={(e) => setForm({ ...form, spending_grocery: parseFloat(e.target.value) })} />
              </div>
              <div>
                <Label className="mb-1.5 block">Dining/mo</Label>
                <Input type="number" min="0" step="50" value={form.spending_dining} onChange={(e) => setForm({ ...form, spending_dining: parseFloat(e.target.value) })} />
              </div>
              <div>
                <Label className="mb-1.5 block">Gas/mo</Label>
                <Input type="number" min="0" step="50" value={form.spending_gas} onChange={(e) => setForm({ ...form, spending_gas: parseFloat(e.target.value) })} />
              </div>
              <div>
                <Label className="mb-1.5 block">Travel/mo</Label>
                <Input type="number" min="0" step="50" value={form.spending_travel} onChange={(e) => setForm({ ...form, spending_travel: parseFloat(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Credit score band</Label>
              <Select value={form.credit_score} onValueChange={(v) => setForm({ ...form, credit_score: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent (740+)</SelectItem>
                  <SelectItem value="good">Good (670-739)</SelectItem>
                  <SelectItem value="fair">Fair (580-669)</SelectItem>
                  <SelectItem value="poor">Building credit (&lt;580)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.annual_fee_ok} onCheckedChange={(v) => setForm({ ...form, annual_fee_ok: !!v })} /> I'm okay with annual fees if rewards offset
            </label>
            <Button onClick={match} disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="cc-match">
              <CreditCard className="w-4 h-4 mr-2" /> {loading ? "Matching…" : "Find my card"}
            </Button>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {result ? (
            <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="w-4 h-4 text-emerald-700" />
                  <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">AI card recommendation</div>
                </div>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap" data-testid="cc-result">{result.recommendation}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {(result.cards || []).slice(0, 4).map((c) => (
                    <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        {c.merchant_logo && <img src={c.merchant_logo} alt={c.merchant_name} className="w-7 h-7 rounded object-contain bg-gray-50 p-0.5 border border-gray-100" />}
                        <span className="font-display font-semibold">{c.merchant_name}</span>
                      </div>
                      <div className="text-sm">{c.title}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-gray-500"><strong>Disclaimer:</strong> This is not financial advice. Offers subject to issuer approval.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-gray-200 shadow-none rounded-2xl">
              <CardContent className="p-12 text-center">
                <CreditCard className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <h3 className="font-display font-bold text-lg">Get a card matched to your life</h3>
                <p className="text-gray-600 text-sm mt-1">Tell us how you spend and we'll rank the best 3 cards.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
